<template lang="pug">
div(v-if='isActive')
    //- Full-viewport backdrop for OVERVIEW steps (no element highlight — just
    //- dim the whole page).  Kept separate from the spotlight slabs so a
    //- transition between styles is a simple v-if swap.
    div.tour-full-backdrop(v-if='stepStyleResolved === "overview"')

    //- SPOTLIGHT: four backdrop slabs around the target rect, ring highlight,
    //- and anchored popover.
    template(v-else-if='targetRect')
        div.tour-backdrop(:style='backdropSlabs.top')
        div.tour-backdrop(:style='backdropSlabs.bottom')
        div.tour-backdrop(:style='backdropSlabs.left')
        div.tour-backdrop(:style='backdropSlabs.right')
        div.tour-highlight(:style='highlightStyle')

    //- Card: either the anchored popover (spotlight) or the centred modal
    //- (overview).  Same content, different container.
    div(
        :class='stepStyleResolved === "overview" ? "tour-modal" : "tour-popover"'
        :style='stepStyleResolved === "overview" ? "" : popoverStyle'
    )
        div.tour-header
            v-icon(size='18' color='primary') mdi-school-outline
            div(style='display: flex; flex-direction: column; min-width: 0;')
                span.tour-title.text-truncate {{ activeTutorial?.title }}
                span.tour-subtitle.text-truncate(v-if='activeTutorial?.description') {{ activeTutorial.description }}
            v-spacer
            v-btn(
                icon='mdi-close'
                size='x-small'
                variant='text'
                color='grey-lighten-2'
                title='Skip this tour'
                @click='skipCurrent'
            )

        div.tour-body(:class='stepStyleResolved === "overview" ? "tour-body-overview" : ""')
            div.tour-step-title {{ currentStep?.title }}
            //- Body: split on paragraph breaks so long content renders as
            //- separate <p> blocks rather than one wall of text.
            p.tour-step-body(v-for='para in bodyParagraphs' :key='para') {{ para }}
            //- Optional bullet list — rendered under the body when the step
            //- author provided one.  Common on overview steps.
            ul.tour-step-bullets(v-if='currentStep?.bullets?.length')
                li(v-for='b in currentStep.bullets' :key='b') {{ b }}

        div.tour-footer
            span.tour-step-progress(v-if='activeTutorial')
                | Step {{ currentStepIndex + 1 }} of {{ activeTutorial.steps.length }}
            v-spacer
            v-btn(
                size='small'
                variant='text'
                color='grey-lighten-2'
                :disabled='!canGoBack'
                @click='goBack'
            ) Back
            v-btn(
                size='small'
                variant='tonal'
                color='primary'
                @click='advanceStep'
            ) {{ canGoNext ? 'Next' : 'Done' }}
            v-btn(
                size='small'
                variant='text'
                color='grey-lighten-2'
                title='Skip the rest of the tour'
                @click='skipCurrent'
            ) Skip
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useTutorial } from './useTutorial';

const POPOVER_WIDTH = 380;
const POPOVER_GAP = 12;
const POPOVER_MAX_HEIGHT = 320;
const HIGHLIGHT_PAD = 6;

const { activeTutorial, currentStep, currentStepIndex, isActive, canGoBack, canGoNext, advanceStep, goBack, skipCurrent, stopTutorial } = useTutorial();

// Style resolution: explicit `style` wins; otherwise infer from presence of
// `target` — matches the intuitive "if you named an element to point at, it's
// a spotlight step" default.
const stepStyleResolved = computed<'spotlight' | 'overview'>(() => {
    const s = currentStep.value;
    if (!s) return 'overview';
    if (s.style) return s.style;
    return s.target ? 'spotlight' : 'overview';
});

// Rect of the current target element in viewport coordinates.  Null for
// overview steps and while polling for a not-yet-mounted spotlight target.
const targetRect = ref<DOMRect | null>(null);
let rafId: number | null = null;
let findAttempts = 0;
const MAX_FIND_ATTEMPTS = 30; // 30 × 100 ms = 3 s

// Re-resolve the target whenever the step changes.  Overview steps skip
// polling entirely — they don't need a target rect to render.
watch(currentStep, (step) => {
    findAttempts = 0;
    if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
    if (!step || stepStyleResolved.value === 'overview' || !step.target) {
        targetRect.value = null;
        return;
    }
    pollForTarget(step.target);
}, { immediate: true });

function pollForTarget(selector: string) {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (el) {
        targetRect.value = el.getBoundingClientRect();
        scheduleTrack();
        return;
    }
    findAttempts++;
    if (findAttempts >= MAX_FIND_ATTEMPTS) {
        console.warn(`[Tutorial] Target '${selector}' not found after ${MAX_FIND_ATTEMPTS * 100} ms — skipping step.`);
        targetRect.value = null;
        advanceStep();
        return;
    }
    window.setTimeout(() => {
        if (!isActive.value || currentStep.value?.target !== selector) return;
        pollForTarget(selector);
    }, 100);
}

// rAF loop to keep the highlight + popover stuck to the target as menus / cards
// animate open.  Cheap — one getBoundingClientRect per frame on a single element.
function scheduleTrack() {
    if (rafId !== null) cancelAnimationFrame(rafId);
    const tick = () => {
        if (!isActive.value || !currentStep.value || !currentStep.value.target) {
            rafId = null;
            return;
        }
        const el = document.querySelector(currentStep.value.target) as HTMLElement | null;
        if (el) {
            const r = el.getBoundingClientRect();
            const prev = targetRect.value;
            if (!prev || prev.x !== r.x || prev.y !== r.y || prev.width !== r.width || prev.height !== r.height) {
                targetRect.value = r;
            }
        }
        rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
}

// Click delegation: clicks that land inside the target element advance the
// tour.  Only wired for spotlight steps — overview steps advance via the Next
// button.  50 ms delay lets the underlying click handler (e.g. opening a
// menu) run before we move on so the next step's target can be found.
function onDocumentClick(e: MouseEvent) {
    if (!isActive.value || !currentStep.value) return;
    if (stepStyleResolved.value !== 'spotlight') return;
    if (!currentStep.value.target) return;
    const target = document.querySelector(currentStep.value.target) as HTMLElement | null;
    if (!target) return;
    const path = e.composedPath();
    if (path.includes(target)) {
        window.setTimeout(advanceStep, 50);
    }
}
document.addEventListener('click', onDocumentClick, true);

onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick, true);
    if (rafId !== null) cancelAnimationFrame(rafId);
});

// ── Paragraph splitting ──────────────────────────────────────────────────
// A blank line is the semantic paragraph break.  We normalise to double-\n
// then split; single \n breaks stay in-flow inside a paragraph.
const bodyParagraphs = computed<string[]>(() => {
    const raw = currentStep.value?.body ?? '';
    return raw
        .split(/\n\s*\n/)
        .map(p => p.trim())
        .filter(Boolean);
});

// ── Backdrop slabs (spotlight only) ──────────────────────────────────────
const backdropSlabs = computed(() => {
    const r = targetRect.value;
    if (!r) {
        return { top: '', bottom: '', left: '', right: '' };
    }
    const pad = HIGHLIGHT_PAD;
    return {
        top:    `top: 0; left: 0; right: 0; height: ${Math.max(0, r.top - pad)}px;`,
        bottom: `top: ${r.bottom + pad}px; left: 0; right: 0; bottom: 0;`,
        left:   `top: ${Math.max(0, r.top - pad)}px; left: 0; width: ${Math.max(0, r.left - pad)}px; height: ${r.height + pad * 2}px;`,
        right:  `top: ${Math.max(0, r.top - pad)}px; left: ${r.right + pad}px; right: 0; height: ${r.height + pad * 2}px;`,
    };
});

const highlightStyle = computed(() => {
    const r = targetRect.value;
    if (!r) return '';
    const pad = HIGHLIGHT_PAD;
    return `top: ${r.top - pad}px; left: ${r.left - pad}px; width: ${r.width + pad * 2}px; height: ${r.height + pad * 2}px;`;
});

// Pick whichever side of the target has more room.  'auto' falls back to
// bottom then right then top then left.
const popoverStyle = computed(() => {
    const r = targetRect.value;
    if (!r) return 'display: none;';
    const step = currentStep.value;
    const placement = step?.placement ?? 'auto';
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const fits = {
        bottom: vh - r.bottom > POPOVER_MAX_HEIGHT + POPOVER_GAP,
        right:  vw - r.right  > POPOVER_WIDTH + POPOVER_GAP,
        top:    r.top         > POPOVER_MAX_HEIGHT + POPOVER_GAP,
        left:   r.left        > POPOVER_WIDTH + POPOVER_GAP,
    } as const;
    const order: Array<keyof typeof fits> = placement === 'auto'
        ? ['bottom', 'right', 'top', 'left']
        : [placement, 'bottom', 'right', 'top', 'left'];
    const chosen = order.find(p => fits[p]) ?? 'bottom';

    let top: number, left: number;
    switch (chosen) {
        case 'bottom':
            top = r.bottom + POPOVER_GAP;
            left = Math.min(Math.max(8, r.left + r.width / 2 - POPOVER_WIDTH / 2), vw - POPOVER_WIDTH - 8);
            break;
        case 'top':
            top = r.top - POPOVER_GAP - POPOVER_MAX_HEIGHT;
            left = Math.min(Math.max(8, r.left + r.width / 2 - POPOVER_WIDTH / 2), vw - POPOVER_WIDTH - 8);
            break;
        case 'right':
            top = Math.min(Math.max(8, r.top + r.height / 2 - POPOVER_MAX_HEIGHT / 2), vh - POPOVER_MAX_HEIGHT - 8);
            left = r.right + POPOVER_GAP;
            break;
        case 'left':
        default:
            top = Math.min(Math.max(8, r.top + r.height / 2 - POPOVER_MAX_HEIGHT / 2), vh - POPOVER_MAX_HEIGHT - 8);
            left = r.left - POPOVER_GAP - POPOVER_WIDTH;
            break;
    }
    return `top: ${top}px; left: ${left}px; width: ${POPOVER_WIDTH}px;`;
});

// If the route changes mid-tour the target will likely vanish — stopTutorial
// returns the user to a clean slate without marking the tutorial seen, so they
// can restart it later if they want.
watch(() => window.location.pathname, () => {
    if (isActive.value) stopTutorial();
});
</script>

<style scoped>
.tour-full-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.62);
    z-index: 10100;
    pointer-events: auto;
}
.tour-backdrop {
    position: fixed;
    background: rgba(0, 0, 0, 0.55);
    z-index: 10100;
    pointer-events: auto;
}
.tour-highlight {
    position: fixed;
    z-index: 10101;
    border: 2px solid #facc15;
    border-radius: 6px;
    box-shadow: 0 0 12px rgba(250, 204, 21, 0.6);
    pointer-events: none;
}

/* Shared card chrome — same look for both variants so switching step styles
   feels like continuity rather than a whole new UI. */
.tour-popover,
.tour-modal {
    position: fixed;
    z-index: 10102;
    background: rgba(20, 20, 28, 0.98);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.7);
    pointer-events: auto;
    display: flex;
    flex-direction: column;
}
/* Overview: centred, wider, taller.  translate(-50%, -50%) with top/left 50%
   is a size-independent centring trick — no JS geometry needed. */
.tour-modal {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(560px, calc(100vw - 40px));
    max-height: min(65vh, 640px);
    overflow: hidden;
}

.tour-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    flex-shrink: 0;
}
.tour-title {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(255, 255, 255, 0.75);
    max-width: 380px;
}
.tour-subtitle {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.45);
    max-width: 380px;
}
.tour-body {
    padding: 12px 14px 10px;
    overflow-y: auto;
    flex: 1 1 auto;
}
.tour-body-overview {
    max-height: 480px;
}
.tour-step-title {
    font-size: 1.05rem;
    font-weight: 600;
    margin-bottom: 10px;
    color: white;
}
.tour-step-body {
    font-size: 0.88rem;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.88);
    margin: 0 0 10px;
}
.tour-step-bullets {
    margin: 8px 0 4px;
    padding-left: 20px;
    font-size: 0.85rem;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.85);
}
.tour-step-bullets li {
    margin-bottom: 4px;
}
.tour-footer {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px 10px 10px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    flex-shrink: 0;
}
.tour-step-progress {
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.5);
    padding-left: 6px;
}
</style>
